import {
  CustomerForecastDiffLastMonthResponseDataInner,
  CustomerForecastWithDiffLastMonth,
  GetCustomerForecastDiffLastMonthResponseAllOfResult,
} from '@/coreInfrastructureModule/axios/generated/customerForecastManage';
import { ICustomerForecastDiffLastMonthResult } from '../domains/entities/customerForecastDiffLastMonthResult';
import {
  ICustomerForecastDiffLastMonthBusinessData,
  ICustomerForecastDiffLastMonthForecastData,
} from '../domains/entities/customerForecastDiffLastMonth';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { ICustomerForecastDiffLastMonthForecastDataFactory } from '../domains/factories/ICustomerForecastDiffLastMonthForecastDataFactory';
import { ICustomerForecastDiffLastMonthDataFactory } from '../domains/factories/ICustomerForecastDiffLastMonthDataFactory';
import { ICustomerForecastDiffLastMonthBusinessDataFactory } from '../domains/factories/ICustomerForecastDiffLastMonthBusinessDataFactory';
import { UserAccount } from '@/coreDomainsModule/entities/users';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';

/**
 * APIから取得したデータをUI用に変換する
 */
export class BulkGetCustomerForecastDiffLastMonthAdapter {
  /**
   * APIから取得したデータをUI用に変換する
   * @param response - APIから取得したデータ
   * @param receivedYearMonth - 受領年月
   * @param targetStartYearMonth - 内示対象開始年月
   * @param targetMonthMaxCount - 内示対象月数
   */
  public static adapt = (
    response: GetCustomerForecastDiffLastMonthResponseAllOfResult,
    receivedYearMonth: IYearMonth,
    targetStartYearMonth: IYearMonth,
    targetMonthMaxCount: number
  ): ICustomerForecastDiffLastMonthResult => {
    const forecastTargetYearMonthList = DateService.getYearMonthsBetween(
      targetStartYearMonth,
      DateService.getShiftedIYearMonthByMonths(targetStartYearMonth.year, targetStartYearMonth.month, targetMonthMaxCount - 1) // countは開始月を含むため-1
    );

    return {
      result: response.data.map((data) => {
        const customerForecasts = this.adaptCustomerForecasts(receivedYearMonth, data.customerAnnounces, forecastTargetYearMonthList);
        return ICustomerForecastDiffLastMonthDataFactory.create({
          ...this.adaptBusiness(data),
          customerForecastUpdateAt: customerForecasts.length > 0 ? customerForecasts[0].customerForecastUpdateAt : undefined,
          customerForecasts: customerForecasts,
        });
      }),
      pagingOptions: response.pagingOptions,
    };
  };

  /**
   * APIから取得したデータをUI用に変換する
   * @param data - APIから取得したデータ
   */
  private static adaptBusiness = (data: CustomerForecastDiffLastMonthResponseDataInner): ICustomerForecastDiffLastMonthBusinessData => {
    return ICustomerForecastDiffLastMonthBusinessDataFactory.create({
      customerForecastStaffList:
        data.baseBusinessResponsibleUsers?.map((user) => UserAccountFactory.create({ email: user.id, name: user.name })) ?? [],
      businessId: data.baseBusinessId,
      supplierNumber: data.supplierNumber,
      supplierName: data.supplierName,
      businessNumber: data.businessNumber,
      businessName: data.businessName,
      package: data.package.addComma(),
      unit: data.unit,
      manufacturer: data.manufacturer,
      warehouseNumber: data.warehouseNumber,
      warehouse: data.warehouse,
      customerNumber: data.customerNumber,
      customerName: data.customerName,
      salesDestinationNumber: data.salesDestinationNumber,
      salesDestinationName: data.salesDestinationName,
      endUser: data.endUser,
      salesUnitPrice: data.salesUnitPrice != null ? Number(data.salesUnitPrice) : undefined,
    });
  };

  /**
   * APIから取得したデータをUI用に変換する
   * @param receivedYearMonth - 受領年月
   * @param forecastList - 内示データ
   * @param targetYearMonthList - 内示対象年月リスト
   */
  private static adaptCustomerForecasts = (
    receivedYearMonth: IYearMonth,
    forecastList: CustomerForecastWithDiffLastMonth[],
    targetYearMonthList: IYearMonth[]
  ): ICustomerForecastDiffLastMonthForecastData[] => {
    const result: ICustomerForecastDiffLastMonthForecastData[] = [];

    targetYearMonthList.forEach((targetYearMonth) => {
      const targetForecast = forecastList.find(
        (forecast) =>
          forecast.customerForecastTargetYear === targetYearMonth.year && forecast.customerForecastTargetMonth === targetYearMonth.month
      );

      result.push(
        ICustomerForecastDiffLastMonthForecastDataFactory.create({
          receivedYearMonth: receivedYearMonth,
          targetYearMonth: targetYearMonth,
          lastMonthAmount: targetForecast?.amountLastMonth,
          currentMonthAmount: targetForecast?.amountTargetMonth,
          diffAmount: targetForecast?.amountDiffValue,
          customerForecastUpdateAt: targetForecast?.updateAt,
        })
      );
    });

    return result;
  };
}
